import { cx } from "@suse-o11y/class-variance";
import { createSlug } from "@suse-o11y/create-slug";
import { Dialog, DialogClose, DialogTitle, useDialogClose } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import { useComputed } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Alert } from "~/components/alert";
import { Form } from "~/components/form";
import { type QueryView, Type } from "~/models/dto";

import { usePersistNodeFlow } from "../../../queries";
import { VIEW_PATH } from "../../../routes/Routes";
import { DocLink } from "../../react/docLink/DocLink";
import { LinkTo } from "../../react/link/LinkTo";
import { notification } from "../../react/notification/Notification";

import { init as initFormState } from "./_form-state";
import { ViewForm } from "./_view-form";

interface Props {
	initial: QueryView;
}

export const ViewFormDrawer: React.FC<Props> = (props) => (
	<Dialog className="drawer/dialog drawer-narrow">
		<ViewFormDrawerContent {...props} />
	</Dialog>
);

const ViewFormDrawerContent: React.FC<Props> = ({ initial }) => {
	const close = useDialogClose();
	const state = usePermanent(() => initFormState(initial));
	const isEditing = initial.id != null;

	const isSubmitting = useComputed((monitor) => state.isSubmitting(monitor), [state]);

	const { mutate: saveView } = usePersistNodeFlow(Type.QueryView, "view", (savedView) => {
		notification.success({
			message:
				initial.id == null ? (
					<>
						The view has been successfully saved as{" "}
						<LinkTo
							data-testid="view-modal-success-toast-view-name"
							params={{ slug: savedView.identifier ?? createSlug(savedView.name) }}
							search={["timeRange", "timestamp", "detachedFilters"]}
							theme="secondary"
							to={VIEW_PATH}
						>
							{savedView.name}
						</LinkTo>
					</>
				) : (
					`The view "${savedView.name}" has been successfully saved.`
				),
		});

		close();
	});

	return (
		<Form
			aria-busy={isSubmitting}
			className={cx(
				"contents",
				"form aria-busy:form-field-reset:form-field-reset-busy",
				"form-field-reset:disabled:form-field-reset-disabled form-field-reset:ml-auto",
				"form-field-label:mb-3 form-field-label:flex form-field-label:items-center form-field-label:gap-1",
				"form-field-message:mt-2",
				"form-fieldset:flex form-fieldset:flex-col form-fieldset:gap-6",
			)}
			onSubmit={async (output) => {
				if (!isSubmitting) {
					await saveView("save", {
						...initial,
						viewType: undefined,
						name: output.name,
						description: output.description,
						identifier: output.identifier,
					});
				}
			}}
			state={state}
		>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">{isEditing ? "Edit view properties" : "Save view as..."}</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section className="drawer-body gap-6">
				{isEditing ? (
					<FormAlert>You are about to update the properties of the current view.</FormAlert>
				) : (
					<FormAlert>
						You are about to save the current view as a new custom view. All the custom views can be found under the{" "}
						<strong className="font-semibold italic">Views</strong> entry in the main menu.
					</FormAlert>
				)}

				<ViewForm initial={initial} state={state} />
			</section>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<button aria-busy={isSubmitting} className="button button-primary uppercase" type="submit">
					{isEditing ? "Update view" : "Create view"}
				</button>
			</footer>
		</Form>
	);
};

const FormAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Alert>
		{children} To learn more about custom views, please read{" "}
		<DocLink capture="noop" slug="ui-k8s-custom-views">
			our documentation
		</DocLink>
		.
	</Alert>
);
